// LintReport schema — produced by ingest worker, consumed by frontend.
//
// Lives in @examforge/shared because both producer (worker · packages/ingest)
// and consumer (frontend · packages/web) must agree on the shape. Single
// source of truth per LLM_NATIVE_ARCHITECTURE.md §5.
//
// Worker writes LintReport into PipelineMeta; frontend zod-parses it from
// the SSE pipeline-complete event. Mirrors the types under
// packages/ingest/src/lint/types.ts (which now re-exports from here).
import { z } from 'zod';
export const LintDimensionSchema = z.enum([
    'mcq_distractor_balance',
    'difficulty_distribution',
    'cognitive_coverage',
    'topic_coverage',
    'citation_trace',
    // 19BG+2 Phase 5 (DEBT-AW-SOURCEREF) · structured citation anchor verification.
    // Distinct from citation_trace (which scans free-form text for fact tokens) —
    // source_ref evaluates the structured `sourceRef.snippet` field against source
    // markdown via whitespace-tolerant substring match.
    'source_ref',
    // 19BG+2 Phase 4 (DEBT-19BH-SHARED-CONCEPTS-SCHEMA) · conceptTag coverage +
    // shared-concept distribution. Drives dispersion metric.
    'shared_concepts',
    // 19BG+2 Phase 2 (DEBT-19BG+2-AUDIT-HEAL-EXTEND) · option-length parity check
    // lifted from quality-eval.ts:107-128 to surface the issue as a lint hint
    // before audit-heal sees it (faster signal · cheaper retry path).
    'option_parity',
]);
export const LintSeveritySchema = z.enum(['info', 'warn', 'error']);
export const LintIssueSchema = z.object({
    severity: LintSeveritySchema,
    dimension: LintDimensionSchema,
    location: z.string(),
    message: z.string(),
    hint: z.string().optional(),
}).passthrough();
export const LintVerdictSchema = z.enum(['high_conf', 'low_conf', 'unknown']);
export const LintReportSchema = z.object({
    generatedAt: z.string(),
    totalIssues: z.number().int().min(0),
    byDimension: z.record(LintDimensionSchema, z.number().int().min(0)),
    bySeverity: z.object({
        info: z.number().int().min(0),
        warn: z.number().int().min(0),
        error: z.number().int().min(0),
    }),
    issues: z.array(LintIssueSchema),
    verdict: LintVerdictSchema,
}).passthrough();
/** Compute aggregate verdict from raw issues. Used by ingest/lint runner. */
export function deriveVerdict(issues) {
    let errors = 0;
    let warns = 0;
    for (const i of issues) {
        if (i.severity === 'error')
            errors++;
        else if (i.severity === 'warn')
            warns++;
    }
    if (errors > 0 || warns >= 5)
        return 'low_conf';
    if (errors === 0 && warns <= 2)
        return 'high_conf';
    return 'unknown';
}
/** Empty per-dimension counter map. */
export function emptyByDimension() {
    return {
        mcq_distractor_balance: 0,
        difficulty_distribution: 0,
        cognitive_coverage: 0,
        topic_coverage: 0,
        citation_trace: 0,
        source_ref: 0,
        shared_concepts: 0,
        option_parity: 0,
    };
}
//# sourceMappingURL=lint-report.js.map